import { ChildRecords } from '@/components/ChildRecords';
import { formatCurrency, formatDate, formatNumber, todayIso } from '@/components/live';
import { useRows } from '@/hooks/useResource';
import { PAYMENT_MODES, QUANTITY_OF, RATE_BASES, WORK_ACTIVITIES, optionLabel } from '@/lib/practiceLists';

interface LabourPayment {
  id: string; payment_code: string | null; worker_id: string; worker_name: string | null; worker_code: string | null;
  activity: string; work_from: string; work_to: string; quantity: number; quantity_basis: string; rate: number;
  amount_due: number; deductions: number; amount_paid: number; balance_due: number; paid_on: string; payment_mode: string;
}
interface WorkerOption { id: string; full_name: string; worker_code: string | null; contractor_id: string | null; status: string }

/**
 * Who worked on a field activity and what they were paid (owner, 2026-09-27: "need to add a tab for
 * selecting the contractor and workers from repository and payment made? connect to contractor and
 * workers?").
 *
 * The contractor is the activity's own "Done by"; the workers come from the Workers register - a
 * contractor's own workers when the activity was done by one - and each line is a worker payment
 * tied to this activity (ops.worker_payments.silviculture_activity_id), so it also shows on the
 * worker's record and in their payment history.
 */
export function ActivityLabour({ activity, link = 'silviculture_activity_id' }: {
  activity: { id: string; plot_id?: string | null; started_on?: string | null; completed_on?: string | null;
              done_by_type?: string | null; done_by_contractor_id?: string | null; done_by_contractor_name?: string | null };
  /** What the payments hang from: a silviculture activity, or a harvest (owner, 2026-09-27: "harvest activity workers"). */
  link?: 'silviculture_activity_id' | 'harvest_id';
}) {
  const noun = link === 'harvest_id' ? 'harvest' : 'activity';
  const contractorId = activity.done_by_type === 'contractor' ? activity.done_by_contractor_id ?? null : null;
  const workers = useRows<WorkerOption>('workers', { filters: { contractor_id: contractorId ?? undefined }, orderBy: { column: 'full_name' } });
  const payments = useRows<LabourPayment>('worker_payments', { filters: { [link]: activity.id } });
  const rows = payments.data ?? [];
  const people = new Set(rows.map((row) => row.worker_id)).size;
  const days = rows.filter((row) => row.quantity_basis === 'per_day').reduce((sum, row) => sum + Number(row.quantity || 0), 0);
  const paid = rows.reduce((sum, row) => sum + Number(row.amount_paid || 0), 0);
  const due = rows.reduce((sum, row) => sum + Number(row.balance_due || 0), 0);

  return (
    <div className="activity-labour">
      <dl className="detail-grid">
        <div><dt>Engaged through</dt><dd>{contractorId ? activity.done_by_contractor_name ?? 'The contractor on Details' : 'Directly (no contractor)'}</dd></div>
        <div><dt>Workers paid</dt><dd>{formatNumber(people)}</dd></div>
        <div><dt>Person-days paid</dt><dd>{formatNumber(days, 1)}</dd></div>
        <div><dt>Paid</dt><dd>{formatCurrency(paid)}{due > 0 ? ` · ${formatCurrency(due)} still due` : ''}</dd></div>
      </dl>
      <ChildRecords<LabourPayment>
        addLabel="Add worker and payment" emptyText={contractorId
          ? `No worker payment for this ${noun} yet. Workers are picked from the contractor's own list.`
          : `No worker payment for this ${noun} yet.`}
        defaults={{ work_from: activity.started_on ?? todayIso(), work_to: activity.completed_on ?? activity.started_on ?? todayIso(),
                    paid_on: todayIso(), payment_mode: 'cash', deductions: '0', quantity_basis: 'per_day' }}
        fields={[
          { key: 'worker_id', label: 'Worker', type: 'select', required: true,
            options: (workers.data ?? []).filter((row) => row.status !== 'inactive')
              .map((row) => ({ id: row.id, label: `${row.full_name}${row.worker_code ? ` (${row.worker_code})` : ''}` })),
            placeholder: workers.isLoading ? 'Loading workers...' : contractorId ? 'The contractor\'s workers' : 'Select from the Workers register' },
          { key: 'activity', label: 'Work', type: 'select', options: WORK_ACTIVITIES, required: true },
          { key: 'quantity_basis', label: 'Rate basis', type: 'select', options: RATE_BASES, required: true },
          { key: 'rate', label: 'Rate (₹)', type: 'number', required: true },
          { key: 'work_from', label: 'Work from', type: 'date', required: true },
          { key: 'work_to', label: 'Work to', type: 'date', required: true },
          { key: 'quantity', label: 'Quantity (days, hours, trees, kg or units)', type: 'number', required: true },
          { key: 'deductions', label: 'Deductions (₹)', type: 'number' },
          { key: 'deduction_reason', label: 'Deduction reason', type: 'text', required: true, showIf: (form) => Number(form.deductions) > 0 },
          { key: 'amount_paid', label: 'Amount paid (₹)', type: 'number', required: true, hint: 'The amount due is worked out from the rate and quantity.' },
          { key: 'paid_on', label: 'Paid on', type: 'date', required: true, max: todayIso() },
          { key: 'payment_mode', label: 'Payment mode', type: 'select', options: PAYMENT_MODES, required: true },
          { key: 'payment_reference', label: 'UPI / bank / cheque reference', type: 'text', required: true,
            showIf: (form) => form.payment_mode !== 'cash' && Boolean(form.payment_mode) },
          { key: 'remarks', label: 'Remarks', type: 'textarea' },
        ]}
        filters={{ [link]: activity.id }} invalidate={[link === 'harvest_id' ? 'plot_harvest_entries' : 'plot_silviculture_activities']} orderBy={{ column: 'paid_on', ascending: false }} ownerId={activity.id}
        parent={{ [link]: activity.id, ...(activity.plot_id ? { plot_id: activity.plot_id } : {}) }}
        screenKey="party.register"
        summary={(row) => ({
          title: `${row.worker_name ?? 'Worker'}${row.worker_code ? ` (${row.worker_code})` : ''} - ${formatCurrency(row.amount_paid)} paid ${formatDate(row.paid_on)}`,
          detail: [`${optionLabel(WORK_ACTIVITIES, row.activity)}`, `${formatDate(row.work_from)} to ${formatDate(row.work_to)}`,
            `${formatNumber(row.quantity, 2)} ${(QUANTITY_OF[row.quantity_basis] ?? '').toLowerCase()} at Rs ${formatNumber(row.rate, 2)}`,
            row.balance_due > 0 ? `balance ${formatCurrency(row.balance_due)}` : null, optionLabel(PAYMENT_MODES, row.payment_mode), row.payment_code]
            .filter(Boolean).join(' · '),
        })}
        title="Workers and payments"
        validate={(form) => ({
          work_to: form.work_to && String(form.work_to) < String(form.work_from) ? 'Cannot end before it starts.' : undefined,
          quantity: form.quantity && !(Number(form.quantity) > 0) ? 'More than 0.' : undefined,
        })}
        table="ops.worker_payments" view="worker_payments" />
    </div>
  );
}
